import { useState } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Select } from './Select';
import type { AddCreditScoreInput } from '../data/useCreditScores';

interface Props {
  /** Submit handler — should perform the network call and resolve when done. */
  onSubmit: (input: AddCreditScoreInput) => Promise<void>;
  onClose: () => void;
  /** Person whose card opened the modal; rendered as context in the header. */
  personLabel: string;
}

const MODEL_SUGGESTIONS = [
  'FICO 8',
  'FICO 9',
  'FICO 2',
  'VantageScore 3.0',
  'VantageScore 4.0',
];

export function CreditScoreModal({ onSubmit, onClose, personLabel }: Props) {
  const [scoreInput, setScoreInput] = useState('');
  const [model, setModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const score = Number(scoreInput);
  const scoreInvalid =
    scoreInput.trim() === '' ||
    !Number.isInteger(score) ||
    score < 300 ||
    score > 850;
  const canSave = !scoreInvalid && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // observed_at is omitted on purpose — the API defaults to today UTC,
      // which is what we want now that the field is no longer user-editable.
      await onSubmit({
        score,
        model: model.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-6 pb-2 flex items-center justify-between gap-3">
        <div className="text-lg font-semibold">Add credit score</div>
        <span className="badge bg-overlay text-fg-tertiary">{personLabel}</span>
      </div>

      <div className="px-6 pb-6 pt-2 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            Score
          </label>
          <input
            type="number"
            min={300}
            max={850}
            step={1}
            inputMode="numeric"
            value={scoreInput}
            onChange={(e) => setScoreInput(e.target.value)}
            autoFocus
            className="px-3 py-2 rounded-lg border border-border bg-subtle outline-none focus:border-border-strong text-sm tabular"
            placeholder="740"
          />
          {scoreInput && scoreInvalid && (
            <span className="text-xs text-red">Score must be a whole number 300–850</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            Model
          </label>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
          >
            <option value="">Select a model</option>
            {MODEL_SUGGESTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </div>

        {error && <div className="text-sm text-red break-words">{error}</div>}
      </div>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="btn btn-outline"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          className="btn btn-primary"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </ModalFooter>
    </Modal>
  );
}
